import React, { useCallback, useEffect, useOptimistic, useRef, useState, useTransition } from "react";
import type { ContactData } from "store/application/applicationSlice";
import type {
    ContactInformationRef,
    CoverageReplacementRef,
    PolicyBeneficiaryRef,
    SecondaryAddresseeRef,
    StatementOfHealthRef,
} from "types/Card";

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface UseContactAutosaveArgs {
    cardRefs: Record<string, React.RefObject<any>>;
    contactInformationRef: React.RefObject<ContactInformationRef | null>;
    statementOfHealthRef: React.RefObject<StatementOfHealthRef | null>;
    coverageReplacementRef: React.RefObject<CoverageReplacementRef | null>;
    policyBeneficiaryRef: React.RefObject<PolicyBeneficiaryRef | null>;
    secondaryAddresseeRef: React.RefObject<SecondaryAddresseeRef | null>;
    isDTO: boolean;
    isMLTA: boolean;
    contactData: ContactData | null;
    saveApplicationData: (data: ContactData) => Promise<void>;
}

/**
 * Per-card autosave on blur with an optimistic "Saving / Saved / Failed"
 * indicator. Collects fresh values from every card ref, persists them, and
 * surfaces the result; the "saved" badge auto-clears after a short delay. The
 * timer is cleared on unmount.
 *
 * Field-level errors are surfaced natively by the form's `mode: "onBlur"` (each
 * field validates only when the user leaves it), so the autosave intentionally
 * does NOT run whole-section validation — leaving a card would otherwise flag
 * every still-empty field at once. Full validation still gates the Next button.
 *
 * @returns The blur handler to wire onto each card container and the optimistic
 *          save status for rendering the indicator.
 */
export const useContactAutosave = ({
    cardRefs,
    contactInformationRef,
    statementOfHealthRef,
    coverageReplacementRef,
    policyBeneficiaryRef,
    secondaryAddresseeRef,
    isDTO,
    isMLTA,
    contactData,
    saveApplicationData,
}: UseContactAutosaveArgs) => {
    const [savedStatus, setSavedStatus] = useState<SaveStatus>("idle");
    const [, startSaveTransition] = useTransition();
    const [optimisticSaveStatus, setOptimisticSave] = useOptimistic(
        savedStatus,
        (_current, newStatus: SaveStatus) => newStatus
    );
    const saveStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleCardBlur = useCallback(
        async (cardId: string) => {
            const cardRef = cardRefs[cardId];
            if (!cardRef?.current) return;

            const freshContactData: ContactData = {
                contactInformation: contactInformationRef.current?.getValues() || null,
                statementOfHealth: isDTO ? statementOfHealthRef.current?.getValues() || null : null,
                coverageReplacement: !isMLTA ? coverageReplacementRef.current?.getValues() || null : null,
                policyBeneficiary: policyBeneficiaryRef.current?.getValues() || null,
                secondaryAddressee: secondaryAddresseeRef.current?.getValues() || null,
                policyNumber: contactData?.policyNumber ?? null,
                hannoverId: contactData?.hannoverId ?? null,
                clubCode: contactData?.clubCode ?? null,
            };

            startSaveTransition(async () => {
                setOptimisticSave("saving");
                try {
                    await saveApplicationData(freshContactData);
                    setSavedStatus("saved");
                    if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
                    saveStatusTimerRef.current = setTimeout(() => setSavedStatus("idle"), 2500);
                } catch {
                    setSavedStatus("error");
                }
            });
        },
        [
            cardRefs,
            contactInformationRef,
            statementOfHealthRef,
            coverageReplacementRef,
            policyBeneficiaryRef,
            secondaryAddresseeRef,
            isDTO,
            isMLTA,
            contactData,
            saveApplicationData,
            startSaveTransition,
            setOptimisticSave,
        ]
    );

    useEffect(() => {
        return () => {
            if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
        };
    }, []);

    return { handleCardBlur, optimisticSaveStatus };
};
